import React, { useEffect, useState } from "react";
import { FaExpand, FaShareAlt, FaTimes } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { GalleryMedia, MediaCaption } from './GalleryMedia';
import { isVideo } from '../utils/galleryMedia';

function getImageSrc(image) {
  return typeof image === "string" ? image : image.url;
}

function getImageKey(image, index) {
  return typeof image === "string" ? image : image.public_id || image.url || index;
}

export default function ImageLightboxGallery({ images, imageLabel }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const openSlideshow = (index) => {
    setCurrentIndex(index);
    setIsOpen(true);
  };

  const closeSlideshow = () => setIsOpen(false);

  const prevImage = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const currentImage = images[currentIndex];
  const currentSrc = currentImage ? getImageSrc(currentImage) : "";

  return (
    <>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {images.map((image, index) => (
            <motion.div
              key={getImageKey(image, index)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.6, delay: (index % 2) * 0.2 }}
              className="relative group overflow-hidden rounded-lg shadow-lg cursor-pointer"
              onClick={() => openSlideshow(index)}
            >
              <GalleryMedia
                media={image}
                alt={`${imageLabel} ${index + 1}`}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              {!isVideo(image) && <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition duration-300 pointer-events-none" />}
              {isVideo(image) && <button type="button" aria-label={`Open video ${index + 1} in full view`} onClick={() => openSlideshow(index)} className="absolute top-2 right-2 rounded bg-black bg-opacity-60 text-white p-2"><FaExpand /></button>}
              <MediaCaption media={image} />
            </motion.div>
          ))}
        </div>
      </main>

      <AnimatePresence>
        {isOpen && currentImage && (
          <motion.div
            key="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center"
          >
            <div className="relative max-w-5xl w-full px-4 sm:px-6">
              <div className="fixed top-4 left-4 z-50 text-white text-sm bg-black bg-opacity-40 px-2 py-1 rounded">
                {currentIndex + 1} / {images.length}
              </div>
              <div className="fixed top-4 right-4 text-white flex items-center gap-3 sm:gap-6 z-50">
                <button
                  onClick={() => {
                    const el = document.documentElement;
                    if (el.requestFullscreen) el.requestFullscreen();
                  }}
                  className="hover:text-yellow-400 p-3"
                  title="Full View"
                  aria-label="Open full screen"
                >
                  <FaExpand />
                </button>

                <button
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({ title: "Fiesta Event Decor", url: currentSrc });
                    } else {
                      navigator.clipboard.writeText(currentSrc);
                      alert("Image link copied to clipboard!");
                    }
                  }}
                  className="hover:text-yellow-400 p-3"
                  title="Share"
                  aria-label="Share image"
                >
                  <FaShareAlt />
                </button>

                <button
                  onClick={closeSlideshow}
                  className="text-3xl hover:text-red-400 p-2"
                  title="Close"
                  aria-label="Close slideshow"
                >
                  <FaTimes />
                </button>
              </div>

              {isVideo(currentImage) ? <GalleryMedia media={currentImage} alt={`${imageLabel} video`} autoPlay className="w-full max-h-[78vh] sm:max-h-[80vh] object-contain mx-auto" /> : <motion.img
                src={currentSrc}
                alt={`${imageLabel} slideshow`}
                key={getImageKey(currentImage, currentIndex)}
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -100, opacity: 0 }}
                className="w-full max-h-[78vh] sm:max-h-[80vh] object-contain mx-auto"
              />}
              <button
                type="button"
                className="absolute top-1/2 left-2 md:-left-12 transform -translate-y-1/2 text-white text-4xl px-5 py-4 cursor-pointer bg-black bg-opacity-30 md:bg-transparent rounded"
                onClick={prevImage}
                aria-label="Previous image"
              >
                &lsaquo;
              </button>
              <button
                type="button"
                className="absolute top-1/2 right-2 md:-right-12 transform -translate-y-1/2 text-white text-4xl px-5 py-4 cursor-pointer bg-black bg-opacity-30 md:bg-transparent rounded"
                onClick={nextImage}
                aria-label="Next image"
              >
                &rsaquo;
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
